import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  ClipboardList, Search, ArrowLeft, Calendar, Clock, User, 
  Download, Filter, QrCode, ArrowRightLeft
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { securityAPI } from '../services/api';

interface VisitRecord {
  id: string;
  date: string;
  time: string;
  type: 'entry' | 'exit';
  visitorName: string;
  visitorPlate?: string;
  residentName: string;
  residentUnit: string;
  scannedBy: string;
  accessMethod: 'qr' | 'manual' | 'list';
}

// Mock data removed — page fetches real data from securityAPI.getVisitors

export default function ReporteVisitas() {
  const navigate = useNavigate();
  const [records, setRecords] = useState<VisitRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<'all' | 'entry' | 'exit'>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const { user, contexts, activeContextId } = useAuth();

  useEffect(() => {
    loadRecords();
  }, [contexts, activeContextId, user?.condoId]);

  const loadRecords = async () => {
    setLoading(true);
    try {
      const active = contexts.find(c => c.id === activeContextId);
      const condoId = active?.condoId || contexts.find(c => c.kind === 'condo')?.condoId || user?.condoId;
      if (!condoId) { setRecords([]); return; }
      const response = await securityAPI.getVisitors(condoId);
      const payload = response?.data?.data ?? response?.data ?? response;
      const rows = Array.isArray(payload) ? payload : payload?.items || payload?.visitors || [];
      setRecords(rows.map((v: any) => {
        const entered = v.entry_time || v.entryTime || v.created_at || v.createdAt;
        const exited = v.exit_time || v.exitTime;
        const date = entered ? new Date(entered) : null;
        return { ...v, type: exited ? 'exit' : 'entry', visitorName: v.name || v.visitorName || 'Visitante', visitorPlate: v.plate || v.visitorPlate, residentName: v.resident_name || v.residentName || v.unit || '—', residentUnit: v.unit || v.residentUnit || '—', scannedBy: v.created_by || v.scannedBy || '—', accessMethod: v.access_method || v.accessMethod || 'manual', date: v.date || (date ? date.toLocaleDateString('es-MX') : '—'), time: v.time || (date ? date.toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' }) : '—') };
      }));
    } catch (err) {
      console.error('Error loading visit records:', err);
      setRecords([]);
    } finally {
      setLoading(false);
    }
  };

  const filteredRecords = records.filter(r => {
    if (filter !== 'all' && r.type !== filter) return false;
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      return (
        r.visitorName.toLowerCase().includes(query) ||
        r.residentName.toLowerCase().includes(query) ||
        r.residentUnit.toLowerCase().includes(query)
      );
    }
    return true;
  });

  return (
    <div className="min-h-full bg-[var(--v-base-bg)]" style={{ fontFamily: 'Inter, sans-serif' }}>
      <header className="sticky top-0 bg-[var(--v-surface-1)] border-b border-[var(--v-border-light)] z-50">
        <div className="flex items-center justify-between h-16 px-6">
          <div className="flex items-center gap-4">
            <button onClick={() => navigate('/home/dashboard')} className="p-2 text-[#2D3748] hover:bg-[var(--v-surface-2)] rounded-xl">
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div>
              <h1 className="text-xl font-bold text-[#2D3748]">Reporte de Visitas</h1>
              <p className="text-xs text-gray-500">Historial de accesos</p>
            </div>
          </div>
          <button className="px-4 py-2 bg-[#36CCD8]/10 text-[#36CCD8] font-bold rounded-xl flex items-center gap-2">
            <Download className="w-4 h-4" /> Exportar
          </button>
        </div>
      </header>

      <main className="p-6 max-w-4xl mx-auto">
        {/* Filters */}
        <div className="bg-[var(--v-surface-1)] rounded-2xl border border-[var(--v-border-light)] shadow-sm p-4 mb-6">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                placeholder="Buscar visitante o residente..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-12 pr-4 py-3 bg-[var(--v-surface-2)] border border-[var(--v-border-light)] rounded-xl focus:border-[#36CCD8] focus:outline-none"
              />
            </div>
            <div className="flex gap-2">
              {(['all', 'entry', 'exit'] as const).map((f) => (
                <button
                  key={f}
                  onClick={() => setFilter(f)}
                  className={`px-4 py-2 rounded-xl font-medium text-sm transition-all ${
                    filter === f
                      ? 'bg-[#36CCD8] text-white'
                      : 'bg-[var(--v-surface-2)] text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  {f === 'all' ? 'Todos' : f === 'entry' ? 'Entradas' : 'Salidas'}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Records */}
        <div className="bg-[var(--v-surface-1)] rounded-2xl border border-[var(--v-border-light)] shadow-sm overflow-hidden">
          <div className="divide-y divide-gray-100">
            {filteredRecords.map(record => (
              <div key={record.id} className="p-4 hover:bg-[var(--v-surface-2)] transition-colors">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                      record.type === 'entry' ? 'bg-emerald-100' : 'bg-red-100'
                    }`}>
                      {record.type === 'entry' ? (
                        <ArrowRightLeft className="w-6 h-6 text-emerald-600" />
                      ) : (
                        <ArrowRightLeft className="w-6 h-6 text-red-600 rotate-180" />
                      )}
                    </div>
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="font-bold text-[#2D3748]">{record.visitorName}</h3>
                        <span className={`px-2 py-0.5 text-xs font-bold rounded-full ${
                          record.type === 'entry' ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'
                        }`}>
                          {record.type === 'entry' ? 'Entrada' : 'Salida'}
                        </span>
                      </div>
                      <p className="text-sm text-gray-500">Residente: {record.residentName} ({record.residentUnit})</p>
                      {record.visitorPlate && (
                        <p className="text-sm text-gray-500">Placa: {record.visitorPlate}</p>
                      )}
                      <div className="flex items-center gap-4 mt-1 text-xs text-gray-400">
                        <span className="flex items-center gap-1"><Calendar className="w-3 h-3" /> {record.date}</span>
                        <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> {record.time}</span>
                        <span className="flex items-center gap-1"><User className="w-3 h-3" /> {record.scannedBy}</span>
                        <span className="flex items-center gap-1"><QrCode className="w-3 h-3" /> {record.accessMethod === 'qr' ? 'QR' : record.accessMethod === 'list' ? 'Lista' : 'Manual'}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
            {filteredRecords.length === 0 && (
              <div className="p-8 text-center text-gray-500">
                <ClipboardList className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                <p>No hay registros</p>
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}
